package org.chiangmeng.qfast.core.ui.customer

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.chiangmeng.qfast.core.domain.ports.QueueRepository
import org.chiangmeng.qfast.core.domain.usecases.QueueStatusUseCase

class CustomerViewModel(
    private val repository: QueueRepository,
    private val statusUseCase: QueueStatusUseCase
) : ScreenModel {

    private val _state = MutableStateFlow(CustomerContract.State())
    val state: StateFlow<CustomerContract.State> = _state.asStateFlow()

    private var pollingJob: Job? = null

    fun handleIntent(intent: CustomerContract.Intent) {
        when (intent) {
            is CustomerContract.Intent.LoadShop -> loadShop(intent.shopId)
            is CustomerContract.Intent.UpdateTicketInput -> updateTicketInput(intent.input)
            is CustomerContract.Intent.IssueTicket -> issueTicket(intent.category)
            CustomerContract.Intent.DismissSuccessDialog -> {
                _state.update { it.copy(newlyIssuedTicket = null) }
            }
            is CustomerContract.Intent.StopObserving -> stopObserving()
            is CustomerContract.Intent.SubmitPreOrder -> submitPreOrder(intent.preOrder)
        }
    }

    private fun submitPreOrder(preOrder: String) {
        val shop = _state.value.shop
        val shopId = shop?.id
        if (shopId == null) {
            println("QFAST_POLL_DEBUG: ❌ SubmitPreOrder failed -> shopId is null")
            return
        }

        if (shop.plan == "FREE") {
            println("QFAST_POLL_DEBUG: ❌ SubmitPreOrder blocked -> shop is on FREE plan")
            return
        }

        val input = _state.value.customerTicketInput
        val ticketNumber = input.filter { it.isDigit() }.toIntOrNull()
            ?: _state.value.newlyIssuedTicket?.toIntOrNull()
            ?: _state.value.queues.firstOrNull()?.number

        if (ticketNumber == null) {
            println("QFAST_POLL_DEBUG: ❌ SubmitPreOrder failed -> ticketNumber not found (input was: '$input')")
            return
        }

        val sanitizedPreOrder = preOrder.trim().take(100)

        screenModelScope.launch {
            try {
                println("QFAST_POLL_DEBUG: 🚀 Sending pre_order to shop: $shopId, ticket: $ticketNumber, text:$sanitizedPreOrder")
                repository.updatePreOrder(shopId, ticketNumber, sanitizedPreOrder)
            } catch (e: Throwable) {
                println("QFAST_POLL_DEBUG: ❌ SubmitPreOrder error -> ${e.message}")
            }
        }
    }

    private fun stopObserving() {
        pollingJob?.cancel()
        pollingJob = null
    }

    private fun loadShop(shopId: String) {
        _state.update { it.copy(isLoading = true) }
        pollingJob?.cancel()
        pollingJob = screenModelScope.launch {
            try {
                while (isActive) {
                    try {
                        val shop = repository.observeShop(shopId).first()
                        if (shop?.plan == "FREE") {
                            _state.update {
                                it.copy(
                                    shop = shop,
                                    queues = emptyList(),
                                    isLoading = false,
                                    statusMessage = "ร้านค้านี้ใช้งานระบบออฟไลน์ (กรุณารอเรียกคิวหน้าร้าน)"
                                )
                            }
                            println("QFAST_POLL_DEBUG: 🛑 Shop is on FREE plan. Stopping background polling.")
                            break
                        }

                        val queues = repository.observeActiveQueues(shopId).first()
                        _state.update {
                            it.copy(
                                shop = shop,
                                queues = queues,
                                isLoading = false
                            )
                        }
                        updateStatusMessage()

                        val input = _state.value.customerTicketInput
                        val myTicket = queues.find {
                            it.number.toString() == input || "${it.category}${it.number}".equals(input, ignoreCase = true)
                        }

                        val delayMillis = when {
                            myTicket?.status == "serving" -> 60_000L
                            myTicket != null -> 30_000L
                            else -> 45_000L
                        }
                        delay(delayMillis)
                    } catch (e: Throwable) {
                        println("QFAST_POLL_DEBUG: Polling error -> ${e.message}")
                        delay(30_000L)
                    }
                }
            } catch (_: CancellationException) {
                // Polling cancelled normally
            }
        }
    }

    private fun updateTicketInput(input: String) {
        _state.update { it.copy(customerTicketInput = input) }
        updateStatusMessage()
    }

    private fun issueTicket(category: String) {
        val shopId = _state.value.shop?.id ?: return
        screenModelScope.launch {
            try {
                val newNumber = repository.issueTicket(shopId, category)
                _state.update {
                    it.copy(
                        newlyIssuedTicket = newNumber.toString(),
                        customerTicketInput = newNumber.toString()
                    )
                }
                updateStatusMessage()
            } catch (e: Exception) {
                // handle error if needed
            }
        }
    }

    private fun updateStatusMessage() {
        val currentState = _state.value
        val shop = currentState.shop ?: return
        val input = currentState.customerTicketInput

        if (input.isBlank()) {
            _state.update { it.copy(statusMessage = null) }
            return
        }

        // คืนหาบัตรคิวในรายการ Active Queues
        val myTicket = currentState.queues.find { 
            it.number.toString() == input || "${it.category}${it.number}".equals(input, ignoreCase = true)
        }
        var message: String? = null

        if (myTicket != null) {
            when (myTicket.status) {
                "serving" -> message = "ถึงคิวของคุณแล้ว (NOW SERVING)"
                "skipped" -> message = "คิวถูกข้าม (SKIPPED)"
                "waiting" -> {
                    // ดึงคิวล่าสุดที่ร้านกำลังเรียกในหมวดหมู่ (category) เดียวกัน
                    val currentServingNum = shop.currentServings[myTicket.category]
                    
                    if (currentServingNum != null && myTicket.number.toString() == currentServingNum.toString()) {
                        message = "ถึงคิวของคุณแล้ว (NOW SERVING)"
                    } else {
                        // ไม่ต้องคำวณ queuesAhead เพราะอาจมีการเรียกสลับตามขนาดโต๊ะ
                        message = if (currentServingNum != null && currentServingNum > 0) {
                            "กำลังรอเรียกคิว (คิวปัจจุบัน: ${myTicket.category}$currentServingNum)"
                        } else {
                            "กำลังรอเรียกคิว..."
                        }
                    }
                }
            }
        } else {
            message = "ไม่พบข้อมูลคิว หรือคิวผ่านไปแล้ว"
        }

        _state.update { it.copy(statusMessage = message) }
    }
}
